import type { ValuationAsset } from '../types/valuation';
import type { GroupingConfig } from '../components/GroupingSelector';
import { generateAssetTypes } from '../data/assetTypes';
import { generateAssetSectors } from '../data/assetSubSectors';
import { generateClassificationTrees, type ClassificationTreeItem } from '../data/classificationTrees';

export interface GroupedData {
  key: string;
  label: string;
  assets: ValuationAsset[];
  subGroups?: GroupedData[];
  total: number;
  count: number;
}

// Create lookup maps for asset types and sub sectors
const createAssetTypeLookup = () => {
  const assetTypes = generateAssetTypes();
  const map = new Map<number, string>();
  assetTypes.forEach(type => {
    map.set(type.id, type.name);
  });
  return map;
};

const createAssetSubSectorLookup = () => {
  const assetSubSectors = generateAssetSectors();
  const map = new Map<number, string>();
  assetSubSectors.forEach(subSector => {
    map.set(subSector.id, subSector.name);
  });
  return map;
};

export const getGroupValue = (asset: ValuationAsset, groupingType: string, groupingConfig?: GroupingConfig): string => {
  switch (groupingType) {
    case 'entity':
      return asset.entityId;
    case 'assetClass': {
      const assetTypeMap = createAssetTypeLookup();
      return asset.assetTypeId ? assetTypeMap.get(asset.assetTypeId) || 'Unclassified' : 'Unclassified';
    }
    case 'sector': {
      const assetSubSectorMap = createAssetSubSectorLookup();
      return asset.assetSubSectorId ? assetSubSectorMap.get(asset.assetSubSectorId) || 'Unclassified' : 'Unclassified';
    }
    case 'portfolio': {
      return asset.portfolioName || 'Unassigned';
    }
    case 'classificationTree': {
      if (!groupingConfig?.treeId || !groupingConfig?.level) {
        return 'Unclassified';
      }
      
      const trees = generateClassificationTrees();
      const tree = trees.find(t => t.id === groupingConfig.treeId);
      
      if (!tree || !tree.items) {
        return 'Unclassified';
      }
      
      // Find which level item contains this asset
      const levelItems = tree.items.filter(item => item.level === groupingConfig.level);
      
      for (const item of levelItems) {
        // Check if asset is directly in this item
        if (item.assetIds && item.assetIds.includes(asset.id)) {
          return item.name;
        }
        
        // Check if asset is in any descendant of this item
        if (isAssetInDescendants(tree.items, item.id, asset.id)) {
          return item.name;
        }
      }
      
      return 'Unclassified';
    }
    default:
      return 'Unknown';
  }
};

// Helper function to check if an asset is in descendants of a tree item
const isAssetInDescendants = (treeItems: ClassificationTreeItem[], parentId: number, assetId: string | number): boolean => {
  const children = treeItems.filter(item => item.parentId === parentId);
  
  for (const child of children) {
    // Check if asset is directly in this child
    if (child.assetIds && child.assetIds.includes(assetId)) {
      return true;
    }
    
    // Recursively check descendants
    if (isAssetInDescendants(treeItems, child.id, assetId)) {
      return true;
    }
  }
  
  return false;
};

export const getGroupLabel = (groupingType: string, value: string, entityName?: string): string => {
  switch (groupingType) {
    case 'entity':
      return entityName || value;
    case 'assetClass':
    case 'sector':
    case 'portfolio':
      return value;
    default:
      return value;
  }
};

export const groupAssets = (
  assets: ValuationAsset[],
  groupings: GroupingConfig[],
  entityNameMap?: Map<string, string>
): GroupedData[] => {
  if (groupings.length === 0) {
    return [{
      key: 'all',
      label: 'All Assets',
      assets,
      total: assets.reduce((sum, asset) => sum + asset.marketValueReporting, 0),
      count: assets.length
    }];
  }

  const [primaryGrouping, ...remainingGroupings] = groupings;
  
  // Group by the primary grouping
  const grouped = new Map<string, ValuationAsset[]>();
  
  assets.forEach(asset => {
    const groupValue = getGroupValue(asset, primaryGrouping.type, primaryGrouping);
    if (!grouped.has(groupValue)) {
      grouped.set(groupValue, []);
    }
    grouped.get(groupValue)!.push(asset);
  });

  // Convert to GroupedData and recursively group sub-items
  return Array.from(grouped.entries()).map(([groupValue, assetsInGroup]) => {
    const total = assetsInGroup.reduce((sum, asset) => sum + asset.marketValueReporting, 0);
    const label = getGroupLabel(primaryGrouping.type, groupValue, entityNameMap?.get(groupValue));
    
    const result: GroupedData = {
      key: groupValue,
      label,
      assets: assetsInGroup,
      total,
      count: assetsInGroup.length
    };

    // If there are more groupings, create sub-groups
    if (remainingGroupings.length > 0) {
      result.subGroups = groupAssets(assetsInGroup, remainingGroupings, entityNameMap);
      result.assets = []; // Clear assets at this level since they're in sub-groups
    }

    return result;
  }).sort((a, b) => {
    // Sort portfolios alphabetically, other groupings by total value
    if (primaryGrouping.type === 'portfolio') {
      return a.label.localeCompare(b.label);
    }
    return b.total - a.total; // Sort by total value descending
  });
};

export const flattenGroupedData = (groupedData: GroupedData[]): ValuationAsset[] => {
  const result: ValuationAsset[] = [];
  
  const flatten = (groups: GroupedData[]) => {
    groups.forEach(group => {
      if (group.subGroups && group.subGroups.length > 0) {
        flatten(group.subGroups);
      } else {
        result.push(...group.assets);
      }
    });
  };
  
  flatten(groupedData);
  return result;
};
